// src/pages/admin/components/sections/CategorySectionEditor.jsx
import React, { useMemo } from "react";
import Field from "./shared/Field";
import HeroImageUploader from "./shared/HeroImageUploader";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

function normalizeTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{toggleLabel || "Toggle"}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const makeNewItem = () => ({
  id: "",
  title: "",
  subtitle: "",
  image: "",
});

export default function CategorySectionEditor({
  config,
  template,
  onChange,
  uploadImage,
  t = (k, f) => f,
}) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeTemplate(template), [template]);

  const items = Array.isArray(cfg.items) ? cfg.items : [];

  const updateItem = (index, key, value) => {
    const next = [...items];
    next[index] = { ...(next[index] || {}), [key]: value };
    onChange?.("items", next);
  };

  const addItem = () => {
    onChange?.("items", [...items, makeNewItem()]);
  };

  const removeItem = (index) => {
    const next = [...items];
    next.splice(index, 1);
    onChange?.("items", next);
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    onChange?.("items", next);
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        {t("categoryEditor.templateInfo.template", "Template")}:{" "}
        <span className="font-semibold text-gray-700">{tplKey}</span>
        {tplKey === "template1" ? ` ${t("categoryEditor.templateInfo.grid", "(Grid)")}` : null}
        {tplKey === "template2" ? ` ${t("categoryEditor.templateInfo.carousel", "(Carousel)")}` : null}
        {tplKey === "template3" ? ` ${t("categoryEditor.templateInfo.spotlight", "(Spotlight)")}` : null}
      </div>

      <Group
        title={t("categoryEditor.group.layoutHeader", "Layout & Header")}
        subtitle={t("categoryEditor.group.layoutHeaderHelp", "Direction, title, and subtitle")}
        toggleLabel={t("categoryEditor.group.layoutHeaderToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("categoryEditor.header.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => onChange?.("direction", v)}
              options={[
                { value: "ltr", label: t("categoryEditor.direction.ltr", "LTR") },
                { value: "rtl", label: t("categoryEditor.direction.rtl", "RTL") },
              ]}
            />

            <div className="text-xs text-gray-500 mt-2">
              {t(
                "categoryEditor.header.layoutDirectionHelp",
                "Use RTL for Arabic/Hebrew and LTR for English."
              )}
            </div>
          </div>

          <Field
            label={t("categoryEditor.header.fields.title", "Title")}
            value={cfg.title || ""}
            onChange={(v) => onChange?.("title", v)}
          />

          <Field
            label={t("categoryEditor.header.fields.subtitle", "Subtitle")}
            type="textarea"
            value={cfg.subtitle || ""}
            onChange={(v) => onChange?.("subtitle", v)}
          />
        </div>
      </Group>

      <Group
        title={t("categoryEditor.group.catalog", "Catalog Connection")}
        subtitle={t("categoryEditor.group.catalogHelp", "Where cards and buttons should open")}
        toggleLabel={t("categoryEditor.group.catalogToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label={t("categoryEditor.catalog.catalogId", "Catalog Id")}
              value={cfg.catalogId || ""}
              onChange={(v) => onChange?.("catalogId", v)}
              placeholder={t("categoryEditor.catalog.catalogIdPlaceholder", "YOUR_CATALOG_ID")}
            />
            <Field
              label={t("categoryEditor.catalog.baseUrl", "Base Url")}
              value={cfg.baseUrl || ""}
              onChange={(v) => onChange?.("baseUrl", v)}
              placeholder={t("categoryEditor.catalog.baseUrlPlaceholder", "https://smartcatalog.shop")}
            />
          </div>

          <div className="text-xs text-gray-500">
            {t(
              "categoryEditor.catalog.hint",
              "Clicking any card (and the main button) opens: baseUrl slash catalogId"
            )}
          </div>
        </div>
      </Group>

      <Group
        title={t("categoryEditor.group.footer", "Footer & CTA")}
        subtitle={t("categoryEditor.group.footerHelp", "Main button, hint text, and item limit")}
        toggleLabel={t("categoryEditor.group.footerToggle", "Toggle")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label={t("categoryEditor.footer.buttonText", "Button Text")}
              value={cfg.buttonText || ""}
              onChange={(v) => onChange?.("buttonText", v)}
              placeholder={
                tplKey === "template2"
                  ? t("categoryEditor.footer.openCatalog", "Open Catalog")
                  : t("categoryEditor.footer.openFullCatalog", "Open Full Catalog")
              }
            />

            <Field
              label={t("categoryEditor.footer.maxItems", "Max Items")}
              type="number"
              value={cfg.maxItems ?? ""}
              onChange={(v) => onChange?.("maxItems", v)}
              placeholder={tplKey === "template2" ? "8" : "6"}
            />
          </div>

          <Field
            label={t("categoryEditor.footer.hintText", "Hint Text")}
            value={cfg.hintText || ""}
            onChange={(v) => onChange?.("hintText", v)}
            placeholder={t("categoryEditor.footer.hintPlaceholder", "Example: You are seeing demo items only")}
          />
        </div>
      </Group>

      <Group
        title={t("categoryEditor.group.displayTexts", "Display Texts")}
        subtitle={t(
          "categoryEditor.group.displayTextsHelp",
          "Fallback labels used inside the category section"
        )}
        toggleLabel={t("categoryEditor.group.displayTextsToggle", "Toggle")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <Field
            label={t("categoryEditor.displayTexts.fallbackItemTitle", "Fallback Item Title")}
            value={cfg.fallbackItemTitle ?? ""}
            onChange={(v) => onChange?.("fallbackItemTitle", v)}
          />

          <Field
            label={t("categoryEditor.displayTexts.featuredBadgeText", "Featured Badge Text")}
            value={cfg.featuredBadgeText ?? ""}
            onChange={(v) => onChange?.("featuredBadgeText", v)}
          />

          <Field
            label={t("categoryEditor.displayTexts.fallbackFeaturedTitle", "Fallback Featured Title")}
            value={cfg.fallbackFeaturedTitle ?? ""}
            onChange={(v) => onChange?.("fallbackFeaturedTitle", v)}
          />

          <Field
            label={t("categoryEditor.displayTexts.viewInCatalogText", "View In Catalog Button Text")}
            value={cfg.viewInCatalogText ?? ""}
            onChange={(v) => onChange?.("viewInCatalogText", v)}
          />

          <Field
            label={t("categoryEditor.displayTexts.demoItemsLabel", "Demo Items Label")}
            value={cfg.demoItemsLabel ?? ""}
            onChange={(v) => onChange?.("demoItemsLabel", v)}
          />
        </div>
      </Group>

      <Group
        title={t("categoryEditor.group.items", "Items")}
        subtitle={t("categoryEditor.group.itemsHelp", "Manage the demo cards shown in this section")}
        toggleLabel={t("categoryEditor.group.itemsToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {t("categoryEditor.items.title", "Items")}
            </h3>

            <button
              type="button"
              onClick={addItem}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
            >
              {t("categoryEditor.items.addItem", "Add Item")}
            </button>
          </div>

          {items.length === 0 ? (
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600">
              {t("categoryEditor.items.empty", "No items yet. Click")}{" "}
              <span className="font-semibold">{t("categoryEditor.items.addItem", "Add Item")}</span>{" "}
              {t("categoryEditor.items.emptyEnd", "to create your first one.")}
            </div>
          ) : null}

          <div className="space-y-4">
            {items.map((it, index) => (
              <div
                key={it?.id ? `${it.id}-${index}` : index}
                className="p-4 border border-gray-200 rounded-xl space-y-4 bg-gray-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-gray-700">
                    {t("categoryEditor.items.item", "Item")} {index + 1}
                    {!isBlank(it?.title) ? (
                      <span className="text-gray-400 font-normal"> • {it.title}</span>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveItem(index, index - 1)}
                      disabled={index === 0}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t("categoryEditor.items.moveUp", "Move up")}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(index, index + 1)}
                      disabled={index === items.length - 1}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t("categoryEditor.items.moveDown", "Move down")}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-500 text-sm hover:underline"
                    >
                      {t("categoryEditor.common.remove", "Remove")}
                    </button>
                  </div>
                </div>

                <Group
                  title={t("categoryEditor.items.group.content", "Item Content")}
                  subtitle={t("categoryEditor.items.group.contentHelp", "Id, title, and subtitle")}
                  toggleLabel={t("categoryEditor.items.group.contentToggle", "Toggle")}
                  defaultOpen
                >
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field
                        label={t("categoryEditor.items.idOptional", "Id (optional)")}
                        value={it?.id || ""}
                        onChange={(v) => updateItem(index, "id", v)}
                        placeholder={t("categoryEditor.items.idPlaceholder", "demo-1")}
                      />
                      <Field
                        label={t("categoryEditor.items.titleField", "Title")}
                        value={it?.title || ""}
                        onChange={(v) => updateItem(index, "title", v)}
                        placeholder={t("categoryEditor.items.titlePlaceholder", "Category title")}
                      />
                    </div>

                    <Field
                      label={t("categoryEditor.items.subtitleOptional", "Subtitle (optional)")}
                      type="textarea"
                      value={it?.subtitle || ""}
                      onChange={(v) => updateItem(index, "subtitle", v)}
                      placeholder={t("categoryEditor.items.subtitlePlaceholder", "Short description")}
                    />
                  </div>
                </Group>

                <Group
                  title={t("categoryEditor.items.group.image", "Item Image")}
                  subtitle={t("categoryEditor.items.group.imageHelp", "Paste a URL or upload an image")}
                  toggleLabel={t("categoryEditor.items.group.imageToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">
                        {t("categoryEditor.image.title", "Image")}
                      </div>

                      <button
                        type="button"
                        onClick={() => updateItem(index, "image", "")}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
                        title={t("categoryEditor.image.removeTitle", "Remove image")}
                      >
                        {t("categoryEditor.image.removeButton", "Remove image")}
                      </button>
                    </div>

                    <Field
                      label={t("categoryEditor.image.urlOptional", "Image URL (optional)")}
                      type="url"
                      value={it?.image || ""}
                      onChange={(v) => updateItem(index, "image", v)}
                      placeholder={t("categoryEditor.image.urlPlaceholder", "Paste image URL, or upload below")}
                    />

                    {it?.image ? (
                      <img
                        src={it.image}
                        alt=""
                        className="w-full max-h-[260px] object-cover rounded-xl border border-gray-200 bg-white"
                      />
                    ) : (
                      <div className="w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                        {t("categoryEditor.image.noImage", "No image selected yet")}
                      </div>
                    )}

                    {uploadImage ? (
                      <HeroImageUploader
                        kind="category"
                        uploadImage={uploadImage}
                        onUploaded={(url) => updateItem(index, "image", url)}
                      />
                    ) : (
                      <div className="text-xs text-gray-500">
                        {t("categoryEditor.image.uploadMissing", "Upload not available: pass")}{" "}
                        <span className="font-mono">uploadImage</span>{" "}
                        {t("categoryEditor.image.uploadMissingEnd", "into this editor.")}
                      </div>
                    )}
                  </div>
                </Group>

                <div className="text-xs text-gray-500">
                  {t(
                    "categoryEditor.items.clickHint",
                    "Clicking the card opens the catalog. These items are for display only."
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Group>
    </div>
  );
}